import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X } from "lucide-react";
import { fmtDate } from "@/components/governance/govUtils";

const STATUSES = ["screening", "not_required", "assessment", "awaiting_approval", "approved", "closed"];
const RES = { low: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-800", high: "bg-red-100 text-red-700" };
const blank = { title: "", processing: "", screening: {}, assessment: { necessity: "", lawful_basis: "", transparency: "", rights: "" }, risks: [], status: "screening", owner: "", review_date: "", dpo_consulted: false, approval_note: "" };

const DpiaDialog = ({ item, questions, onClose, onSaved }) => {
  const [f, setF] = useState(item);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(item), [item]);
  if (!f) return null;
  const hits = questions.filter((q) => f.screening[q.id]);
  const required = hits.some((q) => q.mandatory) || hits.length >= 2;
  const setRisk = (i, k, v) => setF({ ...f, risks: f.risks.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });

  const save = async () => {
    setBusy(true);
    const body = { ...f }; ["id", "_id", "history", "created_at", "created_by", "updated_at", "screening_result", "approved_by", "approved_at"].forEach((k) => delete body[k]);
    try { f.id ? await api.put(`/governance/dpias/${f.id}`, body) : await api.post("/governance/dpias", body); toast.success("DPIA saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="gov-dpia-dialog">
        <DialogHeader><DialogTitle>{f.id ? "DPIA" : "New DPIA screening"}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-3 gap-2">
          <div className="md:col-span-2"><Label className="text-xs">Processing being assessed</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="gov-dpia-title" /></div>
          <div><Label className="text-xs">Status</Label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="h-9 w-full rounded-md border border-border bg-white px-2 text-sm" data-testid="gov-dpia-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select></div>
        </div>
        <div><Label className="text-xs">Nature, scope, context and purpose</Label><Textarea rows={3} value={f.processing} onChange={(e) => setF({ ...f, processing: e.target.value })} data-testid="gov-dpia-processing" /></div>
        <div className="rounded-md border border-border p-3">
          <p className="text-sm font-semibold mb-2">Screening (ICO high-risk indicators)</p>
          <div className="grid md:grid-cols-2 gap-1.5">
            {questions.map((q) => (
              <label key={q.id} className="flex items-start gap-2 text-xs"><Checkbox checked={!!f.screening[q.id]} onCheckedChange={(v) => setF({ ...f, screening: { ...f.screening, [q.id]: !!v } })} data-testid={`gov-dpia-q-${q.id}`} />
                <span>{q.label}{q.mandatory && <strong className="text-red-600"> (mandatory trigger)</strong>}</span></label>
            ))}
          </div>
          <p className={`mt-2 text-xs font-semibold ${required ? "text-red-700" : "text-emerald-700"}`} data-testid="gov-dpia-result">{required ? "A full DPIA is required." : "A full DPIA is not indicated: record why and re-screen if the processing changes."} ({hits.length} indicator{hits.length === 1 ? "" : "s"})</p>
        </div>
        {(required || f.status !== "screening") && (
          <div className="grid md:grid-cols-2 gap-2">
            {[["necessity", "Necessity and proportionality"], ["lawful_basis", "Lawful basis"], ["transparency", "Transparency (notices)"], ["rights", "Individual rights and freedoms"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs">{l}</Label><Textarea rows={2} value={f.assessment?.[k] || ""} onChange={(e) => setF({ ...f, assessment: { ...f.assessment, [k]: e.target.value } })} data-testid={`gov-dpia-${k}`} /></div>
            ))}
          </div>
        )}
        <div>
          <div className="flex items-center justify-between"><p className="text-sm font-semibold">Risks</p>
            <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setF({ ...f, risks: [...f.risks, { risk: "", likelihood: "possible", severity: "significant", mitigation: "", residual: "medium", status: "open" }] })} data-testid="gov-dpia-add-risk"><Plus size={12} /> Add risk</Button></div>
          {f.risks.map((r, i) => (
            <div key={i} className="mt-2 grid md:grid-cols-12 gap-1.5 items-start" data-testid={`gov-dpia-risk-${i}`}>
              <Input className="md:col-span-4 h-8 text-xs" placeholder="Risk to individuals" value={r.risk} onChange={(e) => setRisk(i, "risk", e.target.value)} />
              <Input className="md:col-span-4 h-8 text-xs" placeholder="Mitigation" value={r.mitigation} onChange={(e) => setRisk(i, "mitigation", e.target.value)} />
              {[["likelihood", ["remote", "possible", "likely", "certain"]], ["severity", ["minimal", "significant", "severe"]], ["residual", ["low", "medium", "high"]]].map(([k, opts]) => (
                <select key={k} value={r[k]} onChange={(e) => setRisk(i, k, e.target.value)} className="md:col-span-1 h-8 rounded-md border border-border bg-white px-1 text-[11px]" title={k} data-testid={`gov-dpia-risk-${i}-${k}`}>{opts.map((o) => <option key={o}>{o}</option>)}</select>
              ))}
              <button type="button" onClick={() => setF({ ...f, risks: f.risks.filter((_, j) => j !== i) })} className="md:col-span-1 h-8 text-red-500"><X size={13} /></button>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-2 items-end">
          <div><Label className="text-xs">Owner</Label><Input value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} /></div>
          <div><Label className="text-xs">Review date</Label><Input type="date" value={f.review_date} onChange={(e) => setF({ ...f, review_date: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-xs pb-2"><Checkbox checked={f.dpo_consulted} onCheckedChange={(v) => setF({ ...f, dpo_consulted: !!v })} data-testid="gov-dpia-dpo" /> DPO / adviser consulted</label>
        </div>
        <div className="flex gap-2"><Button disabled={busy || !f.title} onClick={save} data-testid="gov-dpia-save">Save</Button><Button variant="ghost" onClick={onClose}>Cancel</Button></div>
      </DialogContent>
    </Dialog>
  );
};

export const GovDpias = () => {
  const [rows, setRows] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/governance/dpias").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/governance/dpia/questions").then(({ data }) => setQuestions(data)); }, []);
  return (
    <div className="space-y-3" data-testid="gov-dpias">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Screen any new or changed processing. Licensees (as controllers) own their DPIAs; product-level assessments here support them.</p>
        <Button size="sm" className="gap-1" onClick={() => setOpen({ ...blank })} data-testid="gov-dpia-new-btn"><Plus size={13} /> New screening</Button>
      </div>
      {rows.map((r) => (
        <button key={r.id} type="button" onClick={() => setOpen(r)} className="w-full text-left bg-white border border-border rounded-md p-4 hover:border-slate-300" data-testid={`gov-dpia-row-${r.id}`}>
          <div className="flex flex-wrap items-center gap-2"><span className="font-medium">{r.title}</span>
            <Badge variant="outline" className="text-[10px]">{r.status.replace("_", " ")}</Badge>
            {r.screening_result?.dpia_required && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">DPIA required</Badge>}
            {r.risks?.map((k, i) => <span key={i} className={`rounded px-1.5 text-[10px] ${RES[k.residual]}`}>{k.residual}</span>)}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{r.screening_result?.rationale} · Review {fmtDate(r.review_date)}</p>
        </button>
      ))}
      {open && <DpiaDialog item={open} questions={questions} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
};
